import { themeQuartz } from 'ag-grid-enterprise';
import { colors } from './colors';

/**
 * AG Grid Theme Configuration
 *
 * This file contains the centralized theme configuration for AG Grid tables
 * throughout the application. It uses the Quartz theme as a base and applies
 * our custom color scheme and styling parameters.
 *
 * Usage:
 * import { agGridTheme } from '../theme/agGridTheme';
 *
 * // In your component:
 * const gridOptions: GridOptions = {
 *   theme: agGridTheme,
 *   // ... other options
 * };
 */

export const agGridTheme = themeQuartz.withParams({
  // Spacing and sizing
  spacing: 4, // Increased spacing for better readability
  rowHeight: 52, // Increased row height for better visibility
  headerHeight: 56, // Increased header height for better visibility

  // Color scheme
  foregroundColor: colors.secondary.text,
  backgroundColor: colors.neutral.white,
  headerBackgroundColor: colors.neutral.white,

  // Borders and separators
  borderColor: 'transparent',

  // Row styling
  oddRowBackgroundColor: colors.neutral.grey[50],
  rowHoverColor: colors.accent.blue + '15',

  // Selection and focus
  rangeSelectionBackgroundColor: colors.accent.blue + '20',
  rangeSelectionBorderColor: colors.accent.blue,

  // Input styling
  inputBorder: colors.neutral.grey[300],

  // Button styling
  buttonBackgroundColor: colors.accent.blue,
});

/**
 * AG Grid CSS Custom Properties
 *
 * These CSS custom properties provide additional styling control
 * beyond what the theme API supports. They should be applied to
 * the grid container element.
 */
export const agGridCSSVariables = {
  // Core sizing
  '--ag-header-height': '56px',
  '--ag-row-height': '48px',
  '--ag-font-size': '14px',
  '--ag-font-family':
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',

  // Header styling - Uses primary gray from theme
  '--ag-header-background-color': colors.primary.background,
  '--ag-header-foreground-color': colors.primary.text,
  '--ag-header-cell-hover-background-color': colors.primary.hover,

  // Row styling
  '--ag-odd-row-background-color': colors.neutral.grey[50],
  '--ag-even-row-background-color': colors.neutral.white,
  '--ag-row-hover-color': colors.accent.blue + '15',
  '--ag-selected-row-background-color': colors.accent.blue + '25',

  // Border and separator styling
  '--ag-border-color': 'transparent',
  '--ag-header-column-separator-color': colors.neutral.grey[300],
  '--ag-row-border-color': colors.neutral.grey[100],

  // Input styling
  '--ag-input-border-color': colors.neutral.grey[300],
  '--ag-input-focus-border-color': colors.accent.blue,
  '--ag-input-focus-box-shadow': `0 0 0 2px ${colors.accent.blue}20`,

  // Button styling
  '--ag-button-background-color': colors.accent.blue,
  '--ag-button-foreground-color': colors.neutral.white,
  '--ag-button-hover-background-color': colors.accent.blue + 'dd',

  // Status bar styling
  '--ag-status-bar-background-color': colors.neutral.grey[50],
  '--ag-status-bar-foreground-color': colors.secondary.text,

  // Context menu styling
  '--ag-menu-background-color': colors.neutral.white,
  '--ag-menu-foreground-color': colors.secondary.text,
  '--ag-menu-hover-background-color': colors.accent.blue + '10',
} as const;

/**
 * AG Grid CSS Classes
 *
 * These CSS class names can be used for additional styling
 * that targets specific grid elements.
 */
export const agGridCSSClasses = {
  // Cell and header classes
  modernCell: 'modern-cell',
  modernHeader: 'modern-header',
  actionCell: 'action-cell',
  leftAlignCell: 'left-align-cell',
  rightAlignHeader: 'right-align-header',
  centerAlignHeader: 'center-align-header',

  // Column-specific header classes (if needed)
  projectHeader: 'project-header',
  orderHeader: 'order-header',
  taskHeader: 'task-header',
  descriptionHeader: 'description-header',
  hoursHeader: 'hours-header',
  actionHeader: 'action-header',
} as const;

/**
 * AG Grid Theme Styles
 *
 * Complete CSS styles for AG Grid theming. This includes all the
 * custom styling that goes beyond what the theme API provides.
 *
 * Usage:
 * import { agGridThemeStyles } from '../theme/agGridTheme';
 *
 * // In your component's style tag:
 * <style>{agGridThemeStyles}</style>
 */
export const agGridThemeStyles = `
  /* Remove outer borders from grid */
  .ag-theme-quartz {
    border: none !important;
    outline: none !important;
  }
  
  .ag-theme-quartz .ag-root-wrapper {
    border: none !important;
    outline: none !important;
  }
  
  .ag-theme-quartz .ag-root {
    border: none !important;
    outline: none !important;
  }
  
  /* Action cell styling */
  .action-cell {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  /* Modern cell and header classes */
  .ag-theme-quartz .modern-cell {
    font-size: 14px;
    line-height: 1.5;
    padding: 14px 16px;
    transition: all 0.2s ease;
  }
  
  .ag-theme-quartz .modern-header {
    font-weight: 600;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${colors.secondary.text};
  }
  
  /* Right-aligned header text */
  .ag-theme-quartz .ag-header-cell[style*="text-align: right"],
  .ag-theme-quartz .right-align-header {
    text-align: right !important;
  }
  
  .ag-theme-quartz .ag-header-cell[style*="text-align: right"] .ag-header-cell-label,
  .ag-theme-quartz .right-align-header .ag-header-cell-label {
    justify-content: flex-end !important;
    display: flex !important;
  }
  
  .ag-theme-quartz .ag-header-cell[style*="text-align: right"] .ag-header-cell-text,
  .ag-theme-quartz .right-align-header .ag-header-cell-text {
    text-align: right !important;
  }
  
  /* Center-aligned header text */
  .ag-theme-quartz .ag-header-cell[style*="text-align: center"],
  .ag-theme-quartz .center-align-header {
    text-align: center !important;
  }
  
  .ag-theme-quartz .ag-header-cell[style*="text-align: center"] .ag-header-cell-label,
  .ag-theme-quartz .center-align-header .ag-header-cell-label {
    justify-content: center !important;
    display: flex !important;
  }
  
  .ag-theme-quartz .ag-header-cell[style*="text-align: center"] .ag-header-cell-text,
  .ag-theme-quartz .center-align-header .ag-header-cell-text {
    text-align: center !important;
  }
  
  /* Header cell styling */
  .ag-theme-quartz .ag-header-cell {
    font-weight: 600;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-right: 1px solid ${colors.neutral.grey[300]};
    cursor: default !important;
    user-select: none;
  }
  
  .ag-theme-quartz .ag-header-cell:last-child {
    border-right: none;
  }
  
  /* Disable header interactions */
  .ag-theme-quartz .ag-header-cell:hover {
    background-color: inherit !important;
    cursor: default !important;
  }
  
  .ag-theme-quartz .ag-header-cell:active {
    background-color: inherit !important;
  }

  .ag-theme-quartz .ag-header-cell-menu-button {
    cursor: pointer !important;
    opacity: 1;
  }

  /* Cell styling */
  .ag-theme-quartz .ag-cell {
    font-size: 14px;
    line-height: 1.4;
    padding: 8px 12px;
    border-right: 1px solid ${colors.neutral.grey[100]};
  }
  
  .ag-theme-quartz .ag-cell:last-child {
    border-right: none;
  }
  
  /* Row styling */
  .ag-theme-quartz .ag-row {
    border-bottom: 1px solid ${colors.neutral.grey[100]};
    transition: background-color 0.2s ease;
  }
  
  /* Striped rows - odd rows */
  .ag-theme-quartz .ag-row-odd {
    background-color: ${colors.neutral.grey[50]};
    border-left: 3px solid transparent;
  }
  
  /* Striped rows - even rows */
  .ag-theme-quartz .ag-row-even {
    background-color: ${colors.neutral.white};
    border-left: 3px solid transparent;
  }
  
  /* Enhanced striped rows with subtle borders */
  .ag-theme-quartz .ag-row-odd .ag-cell {
    border-right: 1px solid ${colors.neutral.grey[100]};
  }
  
  .ag-theme-quartz .ag-row-even .ag-cell {
    border-right: 1px solid ${colors.neutral.grey[100]};
  }
  
  /* Hover effect for all rows */
  .ag-theme-quartz .ag-row:hover {
    background-color: ${colors.accent.blue}15 !important;
    box-shadow: inset 0 0 0 1px ${colors.accent.blue}30;
  }
  
  /* Hover effect for odd rows */
  .ag-theme-quartz .ag-row-odd:hover {
    background-color: ${colors.accent.blue}15 !important;
  }
  
  /* Hover effect for even rows */
  .ag-theme-quartz .ag-row-even:hover {
    background-color: ${colors.accent.blue}15 !important;
  }
  
  /* Selected row styling */
  .ag-theme-quartz .ag-row-selected {
    background-color: ${colors.accent.blue}25 !important;
    border-left: 4px solid ${colors.accent.blue};
  }
  
  .ag-theme-quartz .ag-row-selected:hover {
    background-color: ${colors.accent.blue}30 !important;
  }
  
  /* Action buttons styling */
  .ag-theme-quartz .action-cell {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0;
  }
  
  .ag-theme-quartz .action-cell button {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    gap: 0.375rem;
    min-width: 70px;
    justify-content: center;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  .ag-theme-quartz .action-cell button:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  .ag-theme-quartz .action-cell button:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }
  
  .ag-theme-quartz .action-cell button svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
  
  /* Left-aligned cell styling */
  .ag-theme-quartz .left-align-cell {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0;
  }
  
  .ag-theme-quartz .left-align-cell button {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    gap: 0.375rem;
    min-width: 70px;
    justify-content: center;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  .ag-theme-quartz .left-align-cell button:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  .ag-theme-quartz .left-align-cell button:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }
  
  .ag-theme-quartz .left-align-cell button svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
  
  /* Status bar styling */
  .ag-theme-quartz .ag-status-bar {
    background-color: ${colors.neutral.grey[50]};
    border-top: 1px solid ${colors.neutral.grey[200]};
    font-size: 12px;
    color: ${colors.secondary.text};
  }
  
  /* Context menu styling */
  .ag-theme-quartz .ag-menu {
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border: 1px solid ${colors.neutral.grey[200]};
  }
  
  .ag-theme-quartz .ag-menu-option {
    padding: 8px 16px;
    font-size: 13px;
    border-radius: 4px;
    margin: 2px 4px;
  }
  
  .ag-theme-quartz .ag-menu-option:hover {
    background-color: ${colors.accent.blue}10;
  }
  
  /* Enhanced cell editing */
  .ag-theme-quartz .ag-cell-inline-editing {
    border: 2px solid ${colors.accent.blue};
    border-radius: 6px;
    box-shadow: 0 0 0 3px ${colors.accent.blue}20;
  }
  
  .ag-theme-quartz .ag-cell-inline-editing input,
  .ag-theme-quartz .ag-cell-inline-editing textarea {
    border: none;
    outline: none;
    background: transparent;
    font-size: 14px;
    font-family: inherit;
    width: 100%;
    padding: 4px 8px;
    border-radius: 4px;
  }
  
  /* Row selection styling */
  .ag-theme-quartz .ag-row-selected {
    background-color: ${colors.accent.blue}15 !important;
    border-left: 4px solid ${colors.accent.blue};
  }
  
  .ag-theme-quartz .ag-row-selected:hover {
    background-color: ${colors.accent.blue}20 !important;
  }
  
  /* Loading overlay styling */
  .ag-theme-quartz .ag-overlay-loading-wrapper {
    background-color: ${colors.neutral.white}dd;
    backdrop-filter: blur(2px);
  }
  
  .ag-theme-quartz .ag-overlay-loading-center {
    background-color: ${colors.neutral.white};
    border: 1px solid ${colors.neutral.grey[200]};
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    padding: 24px;
  }
  
  /* Scrollbar styling */
  .ag-theme-quartz ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  .ag-theme-quartz ::-webkit-scrollbar-track {
    background: ${colors.neutral.grey[100]};
    border-radius: 4px;
  }
  
  .ag-theme-quartz ::-webkit-scrollbar-thumb {
    background: ${colors.neutral.grey[300]};
    border-radius: 4px;
  }
  
  .ag-theme-quartz ::-webkit-scrollbar-thumb:hover {
    background: ${colors.neutral.grey[400]};
  }
`;

/**
 * AG Grid Container Styles
 *
 * Default styles for AG Grid container elements.
 * These can be applied to the grid wrapper div.
 */
export const agGridContainerStyles = {
  height: '500px',
  width: '100%',
  minHeight: '300px',
  border: 'none',
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
} as const;

/**
 * AG Grid Default Column Definition
 *
 * Default column configuration that can be reused across
 * different AG Grid instances.
 */
export const agGridDefaultColDef = {
  resizable: false,
  sortable: false,
  filter: false,
  floatingFilter: false,
  editable: false,
  cellClass: 'modern-cell',
  headerClass: 'modern-header',
  suppressKeyboardEvent: (params: any) => {
    return params.event.key === 'Enter' && !params.editing;
  },
} as const;

/**
 * AG Grid Default Grid Options
 *
 * Default grid options that can be reused across
 * different AG Grid instances.
 */
export const agGridDefaultOptions: any = {
  // Height settings
  rowHeight: 52,
  headerHeight: 56,

  // Performance optimizations
  suppressRowVirtualisation: false,
  suppressColumnVirtualisation: false,
  rowBuffer: 10,
  suppressMaxRenderedRowRestriction: false,
  suppressAnimationFrame: false,

  // Enterprise features - updated to new API
  cellSelection: {
    enabled: true,
    handle: true,
  },

  // Tool panels disabled
  sideBar: false,

  // Context menu suppression
  suppressContextMenu: true,

  // Row grouping
  groupDefaultExpanded: -1,

  // Pivot
  pivotMode: false,
  pivotPanelShow: 'always',

  // Status bar
  statusBar: false,

  // Other options
  animateRows: true,
  enableCellTextSelection: true,
  ensureDomOrder: true,
} as const;
